/**
 * Terms of Service Page
 * CHOMP Pet Dental - Veterinary Clinic
 */

import React from 'react';
import Footer from '../components/Footer';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-brand-teal text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Terms of Service
          </h1>
          <p className="text-xl opacity-90">
            Last Updated: May 22, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <p className="lead text-xl text-gray-700 mb-8">
              These Terms of Service ("Terms") govern your use of the CHOMP Pet Dental website (<a href="https://chomp.vet" className="text-brand-teal hover:underline">chomp.vet</a>) and veterinary services. By accessing our website or using our services, you agree to be bound by these Terms.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">1. About Our Services</h2>
            <p className="text-gray-700 mb-4">
              CHOMP Pet Dental, operating as a veterinary practice in Fort Lauderdale, Florida, provides specialized veterinary dental services for dogs and cats, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Routine dental cleanings and prophylaxis</li>
              <li>Dental X-rays and diagnostics</li>
              <li>Tooth extractions</li>
              <li>Oral surgery</li>
              <li>Treatment of periodontal disease</li>
              <li>Dental health consultations</li>
            </ul>
            <p className="text-gray-700 mb-4">
              All services are provided by licensed veterinarians and veterinary technicians in accordance with Florida veterinary practice regulations.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">2. Client Responsibilities</h2>
            
            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Appointment Scheduling</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>You are responsible for scheduling appointments in advance</li>
              <li>Appointments may be subject to availability</li>
              <li>You must provide accurate information about your pet's health history and current medications</li>
              <li>You must inform us of any changes to your contact information</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Cancellation and No-Show Policy</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Cancellation:</strong> We require at least 48 hours' notice for appointment cancellations. Late cancellations or no-shows may be subject to a cancellation fee.</li>
              <li><strong>Rescheduling:</strong> We will make reasonable efforts to accommodate rescheduling requests</li>
              <li><strong>Repeated No-Shows:</strong> Multiple no-shows may result in restricted booking privileges</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Owner Consent and Authorization</h3>
            <p className="text-gray-700 mb-4">
              By using our services, you represent that you are:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>The legal owner of the pet or authorized to make medical decisions on behalf of the pet</li>
              <li>At least 18 years of age</li>
              <li>Able to provide informed consent for veterinary treatment</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">3. Veterinary Services and Medical Consent</h2>
            
            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Anesthesia and Risk Acknowledgment</h3>
            <p className="text-gray-700 mb-4">
              You understand and acknowledge that:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Most dental procedures require general anesthesia</li>
              <li>Anesthesia carries inherent risks, including but not limited to adverse reactions, complications, and in rare cases, death</li>
              <li>Pre-anesthetic bloodwork and examinations help minimize these risks but cannot eliminate them entirely</li>
              <li>Our team will monitor your pet continuously during anesthesia</li>
              <li>You will be provided with anesthesia consent forms explaining risks and procedures</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Treatment Plans and Estimates</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Treatment cost estimates are provided in good faith but may change based on findings during examination or procedure</li>
              <li>Additional procedures discovered during dental cleaning (e.g., extractions) will be discussed with you when possible</li>
              <li>In cases where immediate treatment is necessary for your pet's health and we cannot reach you, we may proceed with treatment</li>
              <li>You are responsible for all charges incurred for your pet's care</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Medical Records</h3>
            <p className="text-gray-700 mb-4">
              Medical records are the property of CHOMP Pet Dental but will be made available to you or your designated veterinarian upon written request. Records will be maintained in accordance with Florida veterinary practice regulations.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">4. Payment Terms</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Payment Due:</strong> Payment is due at the time services are rendered unless other arrangements have been made in advance</li>
              <li><strong>Accepted Payment Methods:</strong> We accept cash, credit cards, debit cards, and approved financing options</li>
              <li><strong>Deposits:</strong> A deposit may be required when scheduling appointments</li>
              <li><strong>Pet Insurance:</strong> If you have pet insurance, we can provide itemized invoices for reimbursement. However, you are responsible for full payment regardless of insurance coverage</li>
              <li><strong>Payment Plans:</strong> Third-party financing options may be available through approved providers (Cherry first; other partners such as CareCredit or Scratchpay may also be offered)</li>
              <li><strong>Returned Payments:</strong> A fee will be charged for any returned checks or declined payment methods</li>
              <li><strong>Collections:</strong> Unpaid balances may be sent to collections, and you will be responsible for all collection costs and legal fees</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">5. Website Use and Restrictions</h2>
            
            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Permitted Use</h3>
            <p className="text-gray-700 mb-4">
              You may use our website to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Learn about our services and team</li>
              <li>Schedule appointments and consultations</li>
              <li>Access educational resources and blog content</li>
              <li>Contact us with questions</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Prohibited Activities</h3>
            <p className="text-gray-700 mb-4">
              You agree NOT to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Use the website for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Transmit viruses, malware, or harmful code</li>
              <li>Scrape, copy, or reproduce website content without permission</li>
              <li>Impersonate another person or entity</li>
              <li>Harass, abuse, or threaten our staff or other clients</li>
              <li>Submit false or misleading information</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              All content on our website, including but not limited to text, graphics, logos, images, videos, and software, is the property of CHOMP Pet Dental or its licensors and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-700 mb-4">
              You may not reproduce, distribute, modify, or create derivative works from our content without express written permission.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">7. Disclaimer of Warranties</h2>
            <p className="text-gray-700 mb-4">
              WHILE WE STRIVE TO PROVIDE THE HIGHEST QUALITY VETERINARY CARE:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Our website and services are provided "as is" without warranties of any kind</li>
              <li>We do not guarantee specific outcomes or results from treatment</li>
              <li>Veterinary medicine is not an exact science, and complications can occur despite proper care</li>
              <li>Information on our website is for educational purposes and does not replace professional veterinary consultation</li>
              <li>We are not liable for any reliance on website content without seeking direct veterinary care</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              To the fullest extent permitted by law:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>CHOMP Pet Dental and its staff shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Our total liability for any claim shall not exceed the amount you paid for the specific service giving rise to the claim</li>
              <li>This limitation applies regardless of the legal theory (contract, tort, negligence, etc.)</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Some states do not allow limitations on implied warranties or liability, so these limitations may not apply to you.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">9. Emergency Services</h2>
            <p className="text-gray-700 mb-4">
              CHOMP Pet Dental specializes in scheduled dental procedures and is NOT an emergency veterinary hospital. If your pet experiences a life-threatening emergency, please contact:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Your primary veterinarian</li>
              <li>An emergency veterinary hospital</li>
              <li>ASPCA Animal Poison Control Center: (888) 426-4435</li>
              <li>Pet Poison Helpline: (855) 764-7661</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">10. Indemnification</h2>
            <p className="text-gray-700 mb-4">
              You agree to indemnify, defend, and hold harmless CHOMP Pet Dental, its owners, employees, and agents from any claims, losses, damages, liabilities, and expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Your use of our website or services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Any false or misleading information you provide</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">11. Dispute Resolution and Governing Law</h2>
            
            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Governing Law</h3>
            <p className="text-gray-700 mb-4">
              These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">Dispute Resolution</h3>
            <p className="text-gray-700 mb-4">
              In the event of a dispute:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>We encourage you to contact us first to resolve the matter informally</li>
              <li>If informal resolution fails, disputes shall be resolved through binding arbitration in Broward County, Florida</li>
              <li>You waive the right to participate in class action lawsuits</li>
              <li>Small claims court actions are permitted for claims within jurisdictional limits</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services constitutes acceptance of the updated Terms. Material changes will be communicated via email or prominent website notice.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">13. Severability</h2>
            <p className="text-gray-700 mb-4">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">14. Entire Agreement</h2>
            <p className="text-gray-700 mb-4">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and CHOMP Pet Dental regarding use of our website and services.
            </p>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">15. SMS Text Messaging Terms</h2>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">1. SMS Consent Communication</h3>
            <p className="text-gray-700 mb-4">
              The information (phone numbers) obtained as part of the SMS consent process will not be shared with third parties for marketing purposes.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">2. Types of SMS Communications</h3>
            <p className="text-gray-700 mb-4">
              If you have consented to receive text messages from Chomp Pet Dental, you may receive messages related to the following:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Customer care</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Example: "Hello, this is CHOMP Pet Dental regarding your pet's appointment. Reply STOP to opt out of SMS messaging from CHOMP Pet Dental."
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">3. Message Frequency</h3>
            <p className="text-gray-700 mb-4">
              Message frequency may vary depending on the type of communication. For example, you may receive up to 5 SMS messages per week related to customer care.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">4. Potential Fees for SMS Messaging</h3>
            <p className="text-gray-700 mb-4">
              Please note that standard message and data rates may apply, depending on your carrier's pricing plan. These fees may vary if the message is sent domestically or internationally.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">5. Opt-In Method</h3>
            <p className="text-gray-700 mb-4">
              You may opt in to receive SMS messages from Chomp Pet Dental in the following ways:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>By submitting an online form and checking the optional SMS consent checkbox</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">6. Opt-Out Method</h3>
            <p className="text-gray-700 mb-4">
              You can opt out of receiving SMS messages at any time. To do so, simply reply "STOP" to any SMS message you receive. Alternatively, you can contact us directly at <a href="tel:+19544663775" className="text-brand-teal hover:underline">(954) 466-3775</a> or <a href="mailto:info@chomp.vet" className="text-brand-teal hover:underline">info@chomp.vet</a> to request removal from our messaging list.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">7. Help</h3>
            <p className="text-gray-700 mb-4">
              If you are experiencing any issues, you can reply with the keyword HELP. Or, you can get help directly from us at <a href="tel:+19544663775" className="text-brand-teal hover:underline">(954) 466-3775</a> or <a href="mailto:info@chomp.vet" className="text-brand-teal hover:underline">info@chomp.vet</a>.
            </p>
            <p className="text-gray-700 mb-4">
              If you do not wish to receive SMS messages, you can choose not to check the SMS consent box on our forms.
            </p>

            <h3 className="text-xl font-semibold text-primary mt-6 mb-3">8. Standard Messaging Disclosures</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Message and data rates may apply.</li>
              <li>You can opt out at any time by texting "STOP."</li>
              <li>For assistance, text "HELP" or visit our <a href="/privacy-policy" className="text-brand-teal hover:underline">Privacy Policy</a> and <a href="/terms-of-service" className="text-brand-teal hover:underline">Terms of Service</a> pages.</li>
              <li>Message frequency may vary.</li>
            </ul>

            <h2 className="text-2xl font-bold text-primary mt-8 mb-4">16. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <p className="font-semibold text-primary mb-2">CHOMP Pet Dental</p>
              <p className="text-gray-700">1800 E Commercial Blvd</p>
              <p className="text-gray-700">Fort Lauderdale, FL 33308</p>
              <p className="text-gray-700 mt-3">
                <strong>Phone:</strong> <a href="tel:+19544663775" className="text-brand-teal hover:underline">(954) 466-3775</a>
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:info@chomp.vet" className="text-brand-teal hover:underline">info@chomp.vet</a>
              </p>
              <p className="text-gray-700">
                <strong>Website:</strong> <a href="https://chomp.vet" className="text-brand-teal hover:underline">chomp.vet</a>
              </p>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-6 mt-8">
              <p className="text-gray-800 font-medium">
                <strong>Important:</strong> By using our website or services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
